import { faBox, faPlus } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useGetPackaging from '../../../api/packaging/useGetPackaging';
import useCreatePackaging from '../../../api/packaging/useCreatePackaging';
import useUpdatePackaging from '../../../api/packaging/useUpdatePackaging';
import GenericPageContainer from '../../../components/agv-generic-page-container';
import SearchFilter from '../../../components/agv-header-search';
import GenericTable from '../../../components/agv-generic-table';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import EmptyList from '../../../components/agv-empty-list';
import AgrivitechPagination from '../../../components/agv-pagination';
import { STATUS_ENUM } from '../../../components/agv-status/status-enum';
import HeaderButton from '../../../components/agv-header-button';
import PackagingCreateModal from './packaging-create-modal';
import PackagingUpdateModal from './packaging-update-modal';

export default function PackagingReferential() {
  const [packaging, setPackaging] = useState(null);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [isOpenUpdate, setIsOpenUpdate] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    limit: 10,
    offset: searchParams.get('offset') || 0,
    query: searchParams.get('query') || '',
  });

  const handleUpdateParam = useCallback((key, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [key]: value };
      if (key !== 'offset') {
        newFilters.offset = 0;
      }
      return newFilters;
    });
  }, []);

  const { data, isLoading, mutate } = useGetPackaging(filters, 300);

  const {
    createPackaging,
    isLoading: isLoadingCreate,
    isSuccess: isSuccessCreate,
  } = useCreatePackaging();

  const {
    updatePackaging,
    isLoading: isLoadingUpdate,
    isSuccess: isSuccessUpdate,
  } = useUpdatePackaging();

  useEffect(() => {
    const params = new URLSearchParams(filters);
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  useEffect(() => {
    if (isSuccessCreate) {
      setIsOpenCreate(false);
      mutate();
    }
  }, [isSuccessCreate, mutate]);

  useEffect(() => {
    if (isSuccessUpdate) {
      setIsOpenUpdate(false);
      mutate();
    }
  }, [isSuccessUpdate, mutate]);

  const handlePackagingFields = (item) => [
    {
      name: 'Code',
      value: item?.code || '-',
      widthClass: 'tw-w-[120px]',
    },
    {
      name: 'Référence produit',
      value: item?.item_key || '-',
      widthClass: 'tw-w-[220px]',
    },
  ];

  const onOpenModal = (value) => {
    setPackaging(value);
    setIsOpenUpdate(true);
  };

  return (
    <>
      <GenericPageContainer
        title={'Palettes consignées - Référentiel'}
        subtitle={
          'Configurez les types de palettes consignées et leur correspondance avec la référence produit.'
        }
        filters={
          <>
            <SearchFilter
              value={filters.query}
              setValue={(value) => handleUpdateParam('query', value)}
              disabled={isLoading}
            />
            <HeaderButton
              onClick={() => setIsOpenCreate(true)}
              text={'Créer'}
              icon={faPlus}
            />
          </>
        }
      >
        {isLoading ? (
          <GenericTableSkeleton numberOfRows={10} hasTitle />
        ) : data?.items?.length > 0 ? (
          <GenericTable
            isLoading={isLoading}
            rows={data.items.map((element) => ({
              title: element.label || 'Sans libellé',
              subtitle: element.code,
              titleWidthClass: 'tw-w-[260px]',
              icon: faBox,
              onClick: () => onOpenModal(element),
              buttonText: 'Détails',
              status: element?.is_active
                ? STATUS_ENUM.POSITIVE
                : STATUS_ENUM.NEGATIVE,
              statusText: element?.is_active ? 'Actif' : 'Inactif',
              fields: handlePackagingFields(element),
            }))}
            iconColor={'tw-text-checklist-common'}
          />
        ) : (
          <EmptyList />
        )}
        <AgrivitechPagination
          data={data}
          setOffset={(value) => handleUpdateParam('offset', value)}
        />
      </GenericPageContainer>

      <PackagingCreateModal
        isOpen={isOpenCreate}
        setIsOpen={setIsOpenCreate}
        createPackaging={createPackaging}
        mainActionIsLoading={isLoadingCreate}
      />

      <PackagingUpdateModal
        isOpen={isOpenUpdate}
        setIsOpen={setIsOpenUpdate}
        updatePackaging={updatePackaging}
        mainActionIsLoading={isLoadingUpdate}
        packaging={packaging}
      />
    </>
  );
}
